import { useState, useRef, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Phone, ArrowRight, RotateCcw, ChevronLeft } from 'lucide-react'
import useAuthStore from '@/stores/authStore'
import { useAuth } from '@/hooks/useAuth'
import Button from '@/components/ui/Button'
import { ROLE_HOME } from '@/config/routes'
import { OTP_LENGTH, OTP_RESEND_SECONDS, PH_PHONE_PREFIX } from '@/config/constants'

// ─── In-app browser detection ────────────────────────────────────────────────

function isInAppBrowser() {
  const ua = navigator.userAgent || ''
  return /FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Viber|Line\/|Twitter|Snapchat|TikTok|MicroMessenger/i.test(ua)
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent || '')
}

function InAppBrowserScreen() {
  const android = isAndroid()
  const url = window.location.href

  function openInChrome() {
    window.location.href = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.android.chrome;end`
  }

  return (
    <div className="min-h-dvh bg-surface-base flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="w-14 h-14 bg-siglat-teal rounded-xl flex items-center justify-center shadow-teal-glow mb-6">
        <span className="text-surface-base text-lg font-black tracking-tight">SK</span>
      </div>

      <h1 className="font-display text-xl font-bold text-ink-primary mb-2">
        Siglat Konstract
      </h1>

      <div className="bg-surface-raised border border-surface-border rounded-xl p-6 mt-4 max-w-sm w-full">
        <div className="text-3xl mb-4">🌐</div>

        <p className="text-sm font-semibold text-ink-primary mb-2">
          Para makapag-login, buksan ang link sa Chrome o Safari.
        </p>
        <p className="text-xs text-ink-muted mb-5">
          To sign in, please open this link in Chrome or Safari browser.
          Google sign-in does not work inside Messenger, Viber, or other apps.
        </p>

        {android ? (
          <button
            onClick={openInChrome}
            className="w-full bg-siglat-teal text-white font-bold text-sm py-3 px-4 rounded-lg hover:bg-siglat-teal/90 transition-colors"
          >
            Open in Chrome →
          </button>
        ) : (
          <div className="bg-surface-overlay border border-surface-border rounded-lg p-4 text-left">
            <p className="text-xs font-semibold text-ink-secondary mb-2">How to open in Safari:</p>
            <ol className="text-xs text-ink-muted space-y-1 list-decimal list-inside">
              <li>Tap the <span className="font-semibold text-ink-secondary">··· menu</span> (three dots) or Share icon</li>
              <li>Select <span className="font-semibold text-ink-secondary">"Open in Browser"</span> or <span className="font-semibold text-ink-secondary">"Open in Safari"</span></li>
            </ol>
          </div>
        )}
      </div>

      <p className="text-xs text-ink-muted mt-6 max-w-xs">
        This is required to keep your account secure. Google does not allow sign-in inside in-app browsers.
      </p>
    </div>
  )
}

// ─── Google G SVG icon ────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

// ─── OTP Input: 6 individual digit boxes ─────────────────────────────────────
function OtpInput({ onComplete }) {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
  const inputs = useRef([])

  const focusNext = (idx) => {
    if (idx < OTP_LENGTH - 1) inputs.current[idx + 1]?.focus()
  }
  const focusPrev = (idx) => {
    if (idx > 0) inputs.current[idx - 1]?.focus()
  }

  const handleChange = (idx, value) => {
    // Only accept digits
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[idx] = digit
    setDigits(next)

    if (digit) {
      focusNext(idx)
      if (idx === OTP_LENGTH - 1 && next.every(Boolean)) {
        onComplete(next.join(''))
      }
    }
  }

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        const next = [...digits]
        next[idx] = ''
        setDigits(next)
      } else {
        focusPrev(idx)
      }
    }
    if (e.key === 'ArrowLeft')  focusPrev(idx)
    if (e.key === 'ArrowRight') focusNext(idx)
  }

  // Handle paste of full OTP
  const handlePaste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    const next = Array(OTP_LENGTH).fill('')
    text.split('').forEach((d, i) => { next[i] = d })
    setDigits(next)
    const lastFilled = Math.min(text.length, OTP_LENGTH - 1)
    inputs.current[lastFilled]?.focus()
    if (text.length === OTP_LENGTH) onComplete(text)
  }

  return (
    <div className="flex gap-3 justify-center" onPaste={handlePaste}>
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => (inputs.current[idx] = el)}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          className="otp-input"
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          autoFocus={idx === 0}
        />
      ))}
    </div>
  )
}

// ─── Resend countdown timer ───────────────────────────────────────────────────
function ResendTimer({ onResend }) {
  const [seconds, setSeconds] = useState(OTP_RESEND_SECONDS)

  useEffect(() => {
    if (seconds <= 0) return
    const t = setTimeout(() => setSeconds(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [seconds])

  if (seconds > 0) {
    return (
      <p className="text-sm text-ink-muted text-center">
        Resend in <span className="text-ink-secondary font-mono">{seconds}s</span>
      </p>
    )
  }

  return (
    <button
      onClick={() => { onResend(); setSeconds(OTP_RESEND_SECONDS) }}
      className="flex items-center gap-1.5 mx-auto text-sm text-siglat-teal hover:underline"
    >
      <RotateCcw className="w-3.5 h-3.5" />
      Resend code
    </button>
  )
}

// ─── Main Login Page ──────────────────────────────────────────────────────────
export default function Login() {
  const location = useLocation()
  const { user, profile, loading } = useAuth()

  const phoneStep    = useAuthStore((s) => s.phoneStep)
  const pendingPhone = useAuthStore((s) => s.pendingPhone)
  const authLoading  = useAuthStore((s) => s.authLoading)
  const error        = useAuthStore((s) => s.error)
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle)
  const sendPhoneOtp     = useAuthStore((s) => s.sendPhoneOtp)
  const verifyPhoneOtp   = useAuthStore((s) => s.verifyPhoneOtp)
  const resetPhoneStep   = useAuthStore((s) => s.resetPhoneStep)
  const clearError       = useAuthStore((s) => s.clearError)

  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [activeTab, setActiveTab] = useState('google') // 'google' | 'phone'

  // In-app browser (Messenger, Viber, WhatsApp, etc.) → show open-in-browser screen
  if (isInAppBrowser()) return <InAppBrowserScreen />

  // Already logged in → redirect
  if (!loading && user && profile) {
    const from = location.state?.from
    const dest = from || ROLE_HOME[profile.role] || '/book'
    return <Navigate to={dest} replace />
  }

  // ── Phone validation ───────────────────────────────────────────────────────
  const validatePhone = (val) => {
    const digits = val.replace(/\D/g, '')
    if (!digits) return 'Phone number is required'
    if (digits.length < 10) return 'Enter a valid 10-digit number'
    if (!digits.startsWith('9') && digits.length === 10)
      return 'PH mobile numbers start with 09...'
    return ''
  }

  const handleSendOtp = async () => {
    clearError()
    const err = validatePhone(phone)
    if (err) { setPhoneError(err); return }
    setPhoneError('')
    await sendPhoneOtp(phone)
  }

  const handleVerifyOtp = async (token) => {
    clearError()
    await verifyPhoneOtp(token)
  }

  const handleResend = () => {
    clearError()
    sendPhoneOtp(phone)
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-dvh bg-surface-base flex flex-col items-center justify-center px-4 py-12">

      {/* Background accent */}
      <div
        className="pointer-events-none fixed inset-0 overflow-hidden"
        aria-hidden
      >
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-siglat-teal/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-siglat-teal/3 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm flex flex-col gap-8 animate-fade-in">

        {/* Logo + headline */}
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 bg-siglat-teal rounded-xl flex items-center justify-center shadow-teal-glow">
            <span className="text-surface-base text-lg font-black tracking-tight">SK</span>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-primary">
              Siglat Konstract
            </h1>
            <p className="text-sm text-ink-muted mt-1">
              Book trusted construction workers in minutes
            </p>
          </div>
        </div>

        {/* Auth card */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-6 shadow-card">

          {/* ── OTP: Enter phone ── */}
          {activeTab === 'phone' && phoneStep === 'enter_phone' && (
            <>
              <button
                onClick={() => { setActiveTab('google'); resetPhoneStep() }}
                className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-secondary mb-5 -ml-1"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              <h2 className="font-display text-lg font-bold text-ink-primary mb-1">
                Enter your number
              </h2>
              <p className="text-sm text-ink-muted mb-5">
                We'll send a one-time code to verify it's you.
              </p>

              <div className="phone-input-wrapper mb-4">
                <div className="phone-prefix">
                  <span className="text-base">🇵🇭</span>
                  <span className="text-ink-secondary">{PH_PHONE_PREFIX}</span>
                </div>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="9171234567"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, ''))
                    setPhoneError('')
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                  className="phone-input"
                  autoComplete="tel"
                  autoFocus
                />
              </div>

              {(phoneError || error) && (
                <p className="text-xs text-siglat-red mb-3 flex items-center gap-1">
                  <span>⚠</span> {phoneError || error}
                </p>
              )}

              <Button
                fullWidth
                size="lg"
                loading={authLoading}
                onClick={handleSendOtp}
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                Send Code
              </Button>
            </>
          )}

          {/* ── OTP: Enter code ── */}
          {activeTab === 'phone' && phoneStep === 'enter_otp' && (
            <>
              <button
                onClick={resetPhoneStep}
                className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-secondary mb-5 -ml-1"
              >
                <ChevronLeft className="w-4 h-4" /> Change number
              </button>

              <h2 className="font-display text-lg font-bold text-ink-primary mb-1">
                Enter the code
              </h2>
              <p className="text-sm text-ink-muted mb-6">
                Sent to{' '}
                <span className="text-ink-secondary font-medium font-mono">
                  {pendingPhone}
                </span>
              </p>

              <OtpInput onComplete={handleVerifyOtp} />

              {error && (
                <p className="text-xs text-siglat-red mt-4 text-center flex items-center justify-center gap-1">
                  <span>⚠</span> {error}
                </p>
              )}

              {authLoading && (
                <p className="text-xs text-ink-muted mt-4 text-center animate-pulse">
                  Verifying…
                </p>
              )}

              <div className="mt-6">
                <ResendTimer onResend={handleResend} />
              </div>
            </>
          )}

          {/* ── Default: Google + Phone tab chooser ── */}
          {activeTab === 'google' && (
            <div className="flex flex-col gap-4">
              <h2 className="font-display text-lg font-bold text-ink-primary">
                Sign in
              </h2>

              {/* Google */}
              <Button
                variant="social"
                fullWidth
                size="lg"
                loading={authLoading}
                onClick={signInWithGoogle}
                leftIcon={<GoogleIcon />}
              >
                Continue with Google
              </Button>

              {/* Divider */}
              <div className="divider-label text-xs uppercase tracking-widest">
                or
              </div>

              {/* Phone */}
              <Button
                variant="secondary"
                fullWidth
                size="lg"
                onClick={() => setActiveTab('phone')}
                leftIcon={<Phone className="w-4 h-4" />}
              >
                Continue with Phone
              </Button>

              {error && (
                <p className="text-xs text-siglat-red text-center flex items-center justify-center gap-1">
                  <span>⚠</span> {error}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Legal footnote */}
        <p className="text-xs text-ink-muted text-center px-4">
          By continuing, you agree to Siglat Konstract's{' '}
          <a href="#" className="underline hover:text-ink-secondary">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="underline hover:text-ink-secondary">Privacy Policy</a>.
        </p>
      </div>
    </div>
  )
}
